import * as React from 'react';
import { Row } from '../../../components/ui';
import {
  SetStyle,
  trackCheckoutStep,
} from '../../../services/account/AccountCheckout.service';
import {AccountCheckoutForm} from './AccountCheckout.component';
import {LoadingComponent, EmptyPlansComponent} from '../../components';
import {useAccountCheckout} from './AccountCheckout.use-case';
import {AccountCheckoutProps} from './AccountCheckout.type';
import {
  TrustBadges,
  ProgressIndicator,
  SocialProof,
  UrgencyBanner,
  GuaranteeSection,
  PlanBenefits,
  StickyCTA,
  ExitIntentModal,
  ImprovedLoading,
  CheckoutSEO,
} from './_components';

const AccountCheckoutEnhanced: React.FC<AccountCheckoutProps> = ({
  accountImage,
  accountLabel,
  accountShortDescription,
  colors,
  dimensions,
  fontSizes,
  plans,
  formToken,
  accountWeightedRating,
  usersSize,
}) => {
  const _useUseCase = useAccountCheckout(plans, accountLabel);
  const formRef = React.useRef<HTMLFormElement>(null);
  const [showExitIntent, setShowExitIntent] = React.useState(true);

  React.useEffect(() => {
    SetStyle({colors, dimensions, fontSizes});
  }, [colors, dimensions, fontSizes]);

  React.useEffect(() => {
    if (_useUseCase.currentStep) {
      trackCheckoutStep(
        _useUseCase.currentStep,
        _useUseCase.selectedPlan?.code || '',
      );
    }
  }, [_useUseCase.currentStep]);

  const handleCtaClick = () => {
    if (formRef.current) {
      const submitButton = formRef.current.querySelector(
        'button[type="submit"]',
      ) as HTMLButtonElement;
      if (submitButton) {
        submitButton.click();
      }
    }
  };

  const handleExitIntentStay = () => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const handleExitIntentLeave = () => {
    setShowExitIntent(false);
  };

  const AccountCheckoutHeader = () => (
    <div className="row">
      <div className="col s12 center">
        <img
          src={accountImage}
          style={{height: 150, width: 150, borderRadius: '50%'}}
          alt={accountLabel}
        />
        <h1>{accountLabel}</h1>
        <p>{accountShortDescription}</p>
        <TrustBadges />
      </div>
    </div>
  );

  const PlanSelectionSection = () => (
    <>
      <ProgressIndicator
        currentStep={_useUseCase.currentStep}
        colors={colors}
      />

      {_useUseCase.selectedPlan && (
        <>
          <UrgencyBanner />
          <PlanBenefits plan={_useUseCase.selectedPlan} />
          <SocialProof
            usersSize={usersSize}
            weightedRating={accountWeightedRating}
          />
        </>
      )}

      <div className="row">
        <div className="col s12 m8 offset-m2">
          <div className="flow-text">Seu Plano</div>
          {!!_useUseCase.filteredPlans && !!_useUseCase.filteredPlans.length ? (
            <PlanOptionsComponent />
          ) : (
            <EmptyPlansComponent />
          )}
        </div>
      </div>
    </>
  );

  const PlanOptionsComponent = () => {
    if (_useUseCase.selectedPlan) {
      return (
        <div
          style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
            <div>
              <strong style={{fontSize: '18px'}}>
                {_useUseCase.selectedPlan.name}
              </strong>
              <div style={{color: '#666', marginTop: '5px'}}>
                R${' '}
                {parseFloat(String(_useUseCase.selectedPlan.value))
                  .toFixed(2)
                  .replace('.', ',')}
              </div>
            </div>
            {_useUseCase.filteredPlans.length > 1 && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  _useUseCase.setCurrentStep('plan');
                  window.scrollTo({top: 0, behavior: 'smooth'});
                }}
                style={{color: colors.primary || '#039be5'}}>
                Trocar plano
              </a>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const AccountCheckoutFormComponent = () => {
    const CheckoutProps = {
      setPurchased: _useUseCase.setPurchased,
      selectedPlan: _useUseCase.selectedPlan,
      setLoading: _useUseCase.setLoading,
      formToken,
      formRef,
    };

    return (
      <>
        <PlanSelectionSection />

        <div className="row">
          <div className="col s12 m8 offset-m2">
            {!_useUseCase.loading && !!_useUseCase.selectedPlan && (
              <>
                <GuaranteeSection />
                <AccountCheckoutForm {...CheckoutProps} />
              </>
            )}
            {!!_useUseCase.loading && !!_useUseCase.selectedPlan && (
              <ImprovedLoading message="Carregando informações do plano..." />
            )}
          </div>
        </div>

        {_useUseCase.selectedPlan && (
          <StickyCTA
            planName={_useUseCase.selectedPlan.name}
            planValue={_useUseCase.selectedPlan.value}
            onCtaClick={handleCtaClick}
            isSubmitting={_useUseCase.loading}
          />
        )}
      </>
    );
  };

  const AccountCheckoutConfirmation = () => (
    <>
      <div style={{textAlign: 'center', padding: '40px 20px'}}>
        <div style={{fontSize: '64px', color: '#4caf50', marginBottom: '20px'}}>
          <i className="material-icons" style={{fontSize: '64px'}}>
            check_circle
          </i>
        </div>
        <h2 style={{color: '#333', marginBottom: '20px'}}>
          🎉 Parabéns! Bem-vindo ao {accountLabel}
        </h2>
        <div className="flow-text" style={{marginBottom: '15px'}}>
          Você adquiriu o plano {_useUseCase.selectedPlan?.name || ''}
        </div>
        <p
          className="center"
          style={{
            fontSize: '16px',
            color: '#666',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto',
          }}>
          Enviamos para seu e-mail uma senha para você acessar. Baixe o
          Aplicativo e faça seu login com a senha enviada e altere em seu
          perfil.
        </p>

        <div
          style={{
            marginTop: '30px',
            padding: '20px',
            background: '#f8f9fa',
            borderRadius: '8px',
            maxWidth: '500px',
            margin: '30px auto 0',
          }}>
          <h3 style={{margin: '0 0 15px 0', color: '#333'}}>
            Próximos Passos:
          </h3>
          <div style={{textAlign: 'left'}}>
            <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
              <span style={{color: '#4caf50', fontWeight: 'bold'}}>1.</span>
              <span>
                Verifique seu e-mail (não esqueça de verificar o spam)
              </span>
            </div>
            <div style={{display: 'flex', gap: '10px', marginBottom: '10px'}}>
              <span style={{color: '#4caf50', fontWeight: 'bold'}}>2.</span>
              <span>Baixe o aplicativo na App Store ou Google Play</span>
            </div>
            <div style={{display: 'flex', gap: '10px'}}>
              <span style={{color: '#4caf50', fontWeight: 'bold'}}>3.</span>
              <span>Faça login e comece sua jornada!</span>
            </div>
          </div>
        </div>

        <TrustBadges />
      </div>
    </>
  );

  return (
    <>
      {/* SEO Optimization with JSON-LD */}
      {_useUseCase.selectedPlan && (
        <CheckoutSEO
          plan={_useUseCase.selectedPlan}
          accountLabel={accountLabel}
          accountDescription={accountShortDescription}
          accountImage={accountImage}
          accountWeightedRating={accountWeightedRating}
          usersSize={usersSize}
        />
      )}

      <div
        id="checkout-app"
        style={{
          backgroundColor: colors?.background || '#EEEEEE',
          minHeight: '100%',
          color: colors.text || '#333333',
        }}>
        <div className="container">
          <AccountCheckoutHeader />
          {_useUseCase.purchased ? (
            <AccountCheckoutConfirmation />
          ) : (
            <AccountCheckoutFormComponent />
          )}
        </div>

        {showExitIntent && !_useUseCase.purchased && (
          <ExitIntentModal
            onStay={handleExitIntentStay}
            onLeave={handleExitIntentLeave}
          />
        )}
      </div>
    </>
  );
};

export default AccountCheckoutEnhanced;
